import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRealtimeAssets } from 'Core/hooks';
// Material UI
import { Grid, Skeleton } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
// Cloud React Components
import { TagsGroup } from 'Components/Tag';
import { Tooltip, Button } from 'Components'
// Utils
import {
  formatOffsetDisplayOdometer,
  formatOffsetDisplayEngineHours,
  copyToClipboard,
  cutNotes,
  vinValidation
} from 'Core/data/Helpers';
// Core
import { HasPermission } from "Core/security"

import LinearProgress from '@mui/material/LinearProgress';
import { checkDeviceData } from "../../../../../core/data/Helpers";

// Actions
import {
  dtcGetDeviceDTC,
  centerOnUnit
} from 'Redux/actions';
// Global styles
import styles from 'Assets/sass/core/vars.scss';
// Styles
import '../infoBubble.scss';
import '../../../../../components/Map/providers/google/InfoWindow/style.scss'
import OdometerView, { getOdometerValue } from 'Modules/.common/components/OdometerView';

const getValidEcuVin = (...values) => {
  for (const value of values) {
    const currentValue = typeof value === 'string' ? value.trim() : '';
    if (!currentValue || currentValue === 'true') {
      continue;
    }

    if (/^0+$/.test(currentValue)) {
      continue;
    }

    return currentValue;
  }

  return '';
};

const Location = (props) => {
  const dispatch = useDispatch();
  const { type, data, messages, loading } = props;
  const isPreview = data?._isPreview && loading;

  const [infoData, setInfoData] = useState({});

  const { followUnit, centerUnitId } = useSelector(state => state.realtimeMapRedux);
  const assets = useRealtimeAssets({ trackFields: ['id', 'name', 'vin', 'ecuVin', 'make', 'model', 'year', 'licensePlate', 'tags'] });
  const user = useSelector(state => state.securityRedux.user);


  

  useEffect(() => {
    if (data) {
      formatData();
    }
  }, [data, assets]);

  const getValidVin = useCallback((...values) => {
    for (const value of values) {
      const currentValue = typeof value === 'string' ? value.trim() : '';
      if (currentValue && currentValue !== 'true') {
        return currentValue;
      }

    }

    return '';
  }, []);

  const getValidEcuVin = useCallback((...values) => {
    for (const value of values) {
      const currentValue = typeof value === 'string' ? value.trim() : '';
      if (!currentValue || currentValue === 'true') {
        continue;
      }

      if (/^0+$/.test(currentValue)) {
        continue;
      }

      return currentValue;
    }

    return '';
  }, []);

  const getValidField = useCallback((...values) => {
    for (const value of values) {
      if (typeof value === 'string') {
        const currentValue = value.trim();
        if (currentValue && currentValue !== 'true') {
          return currentValue;
        }

        continue;
      }

      if (value !== null && value !== undefined && value !== true) {
        return value;
      }
    }

    return '';
  }, []);
  const formatData = useCallback(() => {
    let newInfoData = {};
    let deviceData = null;
    //get asset info
    const unitId = data?.markerDetail?.unit?.id;
    const asset = assets ? assets?.find(a => a.unitId === unitId) : null;
    const useEcuVin = Boolean(data?.markerDetail?.unit?.useEcuVin);
    const assetVin = getValidVin(asset?.vin, data?.markerDetail?.unit?.asset?.vin);
    const ecuVin = getValidEcuVin(asset?.ecuVin, data?.markerDetail?.unit?.lastReading?.ecuVin, data?.markerDetail?.unit?.asset?.ecuVin);
    const effectiveVin = useEcuVin ? (ecuVin || assetVin) : assetVin;
    const licensePlate = getValidField(asset?.licensePlate, data?.markerDetail?.unit?.asset?.licensePlate);

    let vehicle = {
      useEcuOdometer: data?.markerDetail?.unit?.useEcuOdometer,
      useEcuEngineHours: data?.markerDetail?.unit?.useEcuEngineHours,
      offsetEcuEngineOdometer: data?.markerDetail?.unit?.offsetEcuEngineOdometer,
      offsetEcuEngineHours: data?.markerDetail?.unit?.offsetEcuEngineHours,
      offsetEngineHours: data?.markerDetail?.unit?.offsetEngineHours,
      offsetEngineOdometer: data?.markerDetail?.unit?.offsetEngineOdometer,
      ecuEngineOdometer: data?.markerDetail?.unit?.lastReading?.ecuEngineOdometer,
      ecuEngineHours: data?.markerDetail?.unit?.lastReading?.ecuEngineHours,
      engineOdometer: data?.markerDetail?.unit?.lastReading?.engineOdometer,
      engineHours: data?.markerDetail?.unit?.lastReading?.engineHours
    };

    if (type === "Vehicle") {
      deviceData = data?.markerDetail?.unit?.lastReading?.deviceData;
      vehicle = {
        ...data?.markerDetail?.unit?.lastReading,
        ...vehicle,
        vin: effectiveVin,
        licensePlate
      };
    } else if (type === "Trail") {
      deviceData = data?.markerDetail?.vehicleTrail?.deviceData;
      vehicle = {
        ...data?.markerDetail?.vehicleTrail,
        ...vehicle,
        ecuEngineOdometer: data?.markerDetail?.vehicleTrail?.ecuEngineOdometer,
        ecuEngineHours: data?.markerDetail?.vehicleTrail?.ecuEngineHours,
        engineOdometer: data?.markerDetail?.vehicleTrail?.engineOdometer,
        engineHours: data?.markerDetail?.vehicleTrail?.engineHours,
        vin: effectiveVin,
        licensePlate
      };
    }

    let engineHours = formatOffsetDisplayEngineHours(vehicle);

    //get data for fuel percentage
    let fuelPercentage = null;
    if (deviceData && HasPermission(user, "pFuelLev", [291])) {
      let deviceDataJson = checkDeviceData(deviceData);      
      fuelPercentage = deviceDataJson?.ecuData?.fuelPercentage || null;
    }

    newInfoData = {
      ...vehicle,
      odometer: getOdometerValue(vehicle, user?.isKph),
      engineHours: getFormattedField(engineHours, 'H'),
      useEcuOdometer: data?.markerDetail?.unit?.useEcuOdometer,
      useEcuEngineHours: data?.markerDetail?.unit?.useEcuEngineHours,
      useEcuVin,
      fuelPercentage: fuelPercentage != null && !isNaN(fuelPercentage) ? parseInt(fuelPercentage) : null 
    }

    setInfoData(newInfoData);
  }, [assets, data, getValidEcuVin, getValidField, getValidVin, type, user?.isKph, user]);

  const getFormattedField = useCallback((field, unit) => {
    if (field) {
      return `${field} ${unit}`;
    }

    return 'N/A';
  });

  const getModel = useCallback(() => {
    let info = [];
    const unitId = data?.markerDetail?.unit?.id;
    const asset = assets ? assets?.find(a => a.unitId === unitId) : null;
    let year = getValidField(asset?.year, data?.markerDetail?.unit?.asset?.year);
    let make = getValidField(asset?.make, data?.markerDetail?.unit?.asset?.make);
    let model = getValidField(asset?.model, data?.markerDetail?.unit?.asset?.model);
    if (year && year !== "true") {
      info.push(year);
    }

    if (make && make !== "true") {
      info.push(make);
    }

    if (model && model !== "true") {
      info.push(model);
    }

    if (!info.length) {
      return 'N/A';
    }

    return `${info.join('/')}`;
  }, [assets, data, getValidField]);

  const unitId = data?.markerDetail?.unit?.id;
  const asset = assets ? assets?.find(a => a.unitId === unitId) : null;
  const displayVin = infoData?.useEcuVin
    ? (getValidEcuVin(asset?.ecuVin, data?.markerDetail?.unit?.lastReading?.ecuVin, data?.markerDetail?.unit?.asset?.ecuVin) || infoData?.vin)
    : infoData?.vin;

  /********************************************************************************
   * Function to show the percentage of the fuel
   * @param {*} props 
   * @returns 
   ********************************************************************************/
  const LinearProgressWithLabel = (props) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ width: '100%', marginRight: '8px' }}>
          <LinearProgress 
            style={{ backgroundColor: percentageToShowColor(props?.value)?.secondary }} 
            sx={{ '& .MuiLinearProgress-bar': { backgroundColor: percentageToShowColor(props?.value)?.main }}} 
            variant="determinate" 
            {...props} 
          />
        </div>
        <div style={{ minWidth: 35 }}>
          <label style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.6)', fontWeight: 400 }}>
            {`${Math.round(props?.value)}%`}
          </label>
        </div>
      </div>
    );
  }

  /********************************************************************************
   * Function to show the color of the percentage
   * @param {number} value
   * @returns {object}
   ********************************************************************************/
  const percentageToShowColor = (value) => {
    if (value <= 15) {
      return { main: 'red', secondary: '#ffcccc' };
    } else if (value > 15 && value <= 30) {
      return { main: '#ffcc00', secondary: '#fbffcc' };
    } else {    
      return  { main: '#48bf65', secondary: '#ccffd7' };
    }
  }

  return (
    <div className='info-window-tab'>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        style={{
          padding: '16px 45px 16px 16px'
        }}
      >
        <Grid item xs>
          <h2 style={{ margin: 0 }}>
            {messages['infoBubble_vehicleInfo']}
          </h2>
        </Grid>

        <Grid item xs>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Grid item>
              {isPreview
                ? <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: '2px' }} />
                : <span style={{
                    backgroundColor: '#005BF8',
                    color: 'white',
                    padding: '2px 4px',
                    textTransform: 'uppercase'
                  }}>
                    {data?.markerDetail?.unit?.deviceCategory}
                  </span>
              }
            </Grid>

            <Grid item xs>
              {isPreview
                ? <Skeleton variant="rectangular" width={40} height={20} sx={{ borderRadius: '10px' }} />
                : <TagsGroup
                    options={(data && data?.markerDetail && data?.markerDetail?.unit?.tags ? data?.markerDetail?.unit?.tags : [])}
                    nameField="label"
                    colorField="color"
                    alwaysGroup={true}
                  />
              }
            </Grid>

            <Grid item>
              <div style={{ cursor: 'pointer' }}>
                {followUnit && data?.markerDetail?.unit?.id == centerUnitId
                  ?
                  <Tooltip title={messages['maputilities_vehiclelist_follow_unit']}>
                    <span>
                      <VisibilityIcon
                        style={{ color: '#00AEEF' }}
                        onClick={() => {
                          dispatch(centerOnUnit(data?.markerDetail?.unit?.id, false));
                        }}
                      />
                    </span>
                  </Tooltip>
                  :
                  <VisibilityOffIcon
                    onClick={() => {
                      dispatch(centerOnUnit(data?.markerDetail?.unit?.id, true))
                    }}
                  />
                }
              </div>
            </Grid>

            {
              (data?.markerDetail?.unit?.jbus == 1 || data?.markerDetail?.unit?.vbus == 1)
                ?
                <Grid item>
                  {
                    (data?.markerDetail?.unit?.dtcCount?.history || data?.markerDetail?.unit?.dtcCount?.active)
                      ?
                      <span
                        className={`info-bubble-badge ${data?.markerDetail?.unit?.dtcCount?.active > 0 ? 'info-bubble-badge-red' : 'info-bubble-badge-black'}`}
                        onClick={() => {
                          dispatch(dtcGetDeviceDTC({
                            device: {
                              id: id,
                              label: data?.markerDetail?.unit?.lastReading?.deviceLabel || data?.markerDetail?.unit?.label
                            },
                            history: (data?.markerDetail?.unit?.dtcCount?.active > 0 ? false : true)
                          }));
                        }}
                      >
                        {messages['infoBubble_dtc']}
                      </span>
                      :
                      <span className={`info-bubble-badge info-bubble-badge-disabled`}>
                        {messages['infoBubble_dtc']}
                      </span>
                  }
                </Grid>
                :
                <Grid item></Grid>
            }
          </Grid>
        </Grid>

      </Grid>

      <div style={{ borderTop: 'solid 1px #e4e4e4' }}></div>

      <div>
        <table className="info-bubble-table">
          <tbody>
            <tr>
              <td>
                {messages['infoBubble_vehicleName']}
              </td>
              <td>
                {isPreview
                  ? <Skeleton variant="rectangular" width="60%" height={24} sx={{ borderRadius: '4px' }} />
                  : <div className="hover-container">
                      <span style={{ paddingRight: 8 }}>
                        {data?.markerDetail?.unit?.label || data?.markerDetail?.unit?.lastReading?.deviceLabel || "N/A"}
                      </span>
                      <Button
                        type="icon"
                        icon="content_copy"
                        onClick={() => copyToClipboard(data?.markerDetail?.unit?.label || data?.markerDetail?.unit?.lastReading?.deviceLabel || "N/A")}
                        fontSize="inherit"
                        className="btn-no-padding hover-item"
                      />
                    </div>
                }
              </td>
            </tr>

            <tr>
              <td>
                {messages['infoBubble_odometer']}
              </td>
              <td>
                {isPreview
                  ? <Skeleton variant="rectangular" width="70%" height={24} sx={{ borderRadius: '4px' }} />
                  : <div className="hover-container">
                      <span style={{ paddingRight: 8 }}>
                        {
                          (infoData?.useEcuOdometer) &&
                          <span
                            className="ecuChip"
                            style={{ background: styles.ecu }}
                          >
                            ECU
                          </span>
                        }
                        {infoData?.odometer || "N/A"}
                      </span>
                      <Button
                        type="icon"
                        icon="content_copy"
                        onClick={() => copyToClipboard(infoData?.odometer || "N/A")}
                        fontSize="inherit"
                        className="btn-no-padding hover-item"
                      />
                    </div>
                }
              </td>
            </tr>

            <tr>
              <td>
                {messages['infoBubble_engineHours']}
              </td>
              <td>
                {isPreview
                  ? <Skeleton variant="rectangular" width="50%" height={24} sx={{ borderRadius: '4px' }} />
                  : <div className="hover-container">
                      <span style={{ paddingRight: 8 }}>
                        {
                          (infoData?.useEcuEngineHours) &&
                          <span
                            className="ecuChip"
                            style={{ background: styles.ecu }}
                          >
                            ECU
                          </span>
                        }
                        {infoData?.engineHours || "N/A"}
                      </span>
                      <Button
                        type="icon"
                        icon="content_copy"
                        onClick={() => copyToClipboard(infoData?.engineHours || "N/A")}
                        fontSize="inherit"
                        className="btn-no-padding hover-item"
                      />
                    </div>
                }
              </td>
            </tr>

            <tr>
              <td>
                {messages['infoBubble_yearMakeModel']}
              </td>
              <td>
                {isPreview
                  ? <Skeleton variant="rectangular" width="55%" height={24} sx={{ borderRadius: '4px' }} />
                  : <div className="hover-container">
                      <span style={{ paddingRight: 8 }}>
                        {getModel()}
                      </span>
                      <Button
                        type="icon"
                        icon="content_copy"
                        onClick={() => copyToClipboard(getModel())}
                        fontSize="inherit"
                        className="btn-no-padding hover-item"
                      />
                    </div>
                }
              </td>
            </tr>

            <tr>
              <td>
                {messages['infoBubble_vin']}
              </td>
              <td>
                {isPreview
                  ? <Skeleton variant="rectangular" width="80%" height={24} sx={{ borderRadius: '4px' }} />
                  : <div className="hover-container">
                      <span style={{ paddingRight: 8 }}>
                        {
                          (infoData?.useEcuVin) &&
                          <span
                            className="ecuChip"
                            style={{ background: styles.ecu }}
                          >
                            ECU
                          </span>
                        }
                        {displayVin || 'N/A'}
                      </span>
                      <Button
                        type="icon"
                        icon="content_copy"
                        onClick={() => copyToClipboard(displayVin || "N/A")}
                        fontSize="inherit"
                        className="btn-no-padding hover-item"
                      />
                    </div>
                }
              </td>
            </tr>

            <tr>
              <td>
                {messages['infoBubble_licensePlate']}
              </td>
              <td>
                {isPreview
                  ? <Skeleton variant="rectangular" width="45%" height={24} sx={{ borderRadius: '4px' }} />
                  : <div className="hover-container">
                      <span style={{ paddingRight: 8 }}>
                        {infoData?.licensePlate && infoData?.licensePlate != 'true' ? infoData?.licensePlate : "N/A"}
                      </span>
                      <Button
                        type="icon"
                        icon="content_copy"
                        onClick={() => copyToClipboard(`${infoData?.licensePlate && infoData?.licensePlate != 'true' ? infoData?.licensePlate : 'N/A'}`)}
                        fontSize="inherit"
                        className="btn-no-padding hover-item"
                      />
                    </div>
                }
              </td>
            </tr>

            {
              infoData?.fuelPercentage != null
              &&
              <tr>
                <td>
                  {messages['infoBubble_fuel']}
                </td>
                <td>
                  <div className="hover-container" style={{ width: '60%' }}>
                    <LinearProgressWithLabel value={infoData?.fuelPercentage} />
                  </div>
                </td>
              </tr>
            }

            <tr>
              <td>
                {messages['infoBubble_notes']}
              </td>
              <td>
                {isPreview
                  ? <Skeleton variant="rectangular" width="90%" height={24} sx={{ borderRadius: '4px' }} />
                  : <div className="hover-container">
                      {
                        data?.markerDetail?.unit?.notes?.length > 52
                          ?
                          <Tooltip title={data?.markerDetail?.unit?.notes}>
                            <span style={{ paddingRight: 8 }}>
                              {cutNotes(data?.markerDetail?.unit?.notes, 52)}
                            </span>
                          </Tooltip>
                          :
                          <span style={{ paddingRight: 8 }}>
                            {data?.markerDetail?.unit?.notes || 'N/A'}
                          </span>
                      }
                      <Button
                        type="icon"
                        icon="content_copy"
                        onClick={() => copyToClipboard(data?.markerDetail?.unit?.notes)}
                        fontSize="inherit"
                        className="btn-no-padding hover-item"
                      />
                    </div>
                }
              </td>
            </tr>


          </tbody>
        </table>
      </div>
    </div>
  )
}

export default Location;
